import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Badge,
  PermissionsProvider,
  Table,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import InvoiceCreateButton from '@/screens/Merchants/Invoices/InvoiceCreateButton';
import {AfriexWalletType} from '@/types';
import {formatValues} from '@/utils/dashboard';

import type {BalanceRow, WalletFundAction} from '../types';

type BusinessBalancesTableProps = {
  businessId: string;
  list: BalanceRow[];
  onRequestFund: (
    walletType: AfriexWalletType,
    action: WalletFundAction,
  ) => void;
};

// A business collects from its own customers through the API, so money reaches
// the collection wallet without us being the source of it. Debiting it is still
// valid; crediting it is not, so that header carries no Credit button.
const WALLETS = [
  {
    type: AfriexWalletType.Withdraw,
    label: 'Payout',
    testId: 'payout',
    canCredit: true,
  },
  {
    type: AfriexWalletType.Deposit,
    label: 'Collection',
    testId: 'collection',
    canCredit: false,
  },
  {type: AfriexWalletType.Fee, label: 'Fee', testId: 'fee', canCredit: true},
] as const;

const ROW = 'business-details-balances-row';

const amountClass = (value: number) => {
  if (value === 0) {
    return 'text-gray-400';
  }
  return value < 0 ? 'font-semibold text-failed-500' : '';
};

const Amount = ({value}: {value: number}) => (
  <span className={`font-mono ${amountClass(value)}`}>
    {formatValues(value, 'money')}
  </span>
);

export default function BusinessBalancesTable({
  businessId,
  list,
  onRequestFund,
}: BusinessBalancesTableProps) {
  return (
    <Table containerDataCy="business-details-balances-table">
      <Thead>
        <Tr enableHover={false}>
          <Th className="py-4 px-6 font-semibold">Currency</Th>
          {WALLETS.map((wallet) => (
            <Th key={wallet.type} isNumeric className="py-4 px-6 font-semibold">
              <p>{wallet.label}</p>
              <PermissionsProvider
                permission={AfriexAdminPermission.BusinessWalletUpdate}
              >
                <div className="mt-2 flex justify-end gap-4 normal-case tracking-normal">
                  {wallet.canCredit && (
                    <button
                      type="button"
                      data-cy={`business-details-balances-${wallet.testId}-credit`}
                      onClick={() => onRequestFund(wallet.type, 'credit')}
                      className="text-sm font-medium text-successful-500 hover:underline"
                    >
                      Credit
                    </button>
                  )}
                  <button
                    type="button"
                    data-cy={`business-details-balances-${wallet.testId}-debit`}
                    onClick={() => onRequestFund(wallet.type, 'debit')}
                    className="text-sm font-medium text-failed-500 hover:underline"
                  >
                    Debit
                  </button>
                </div>
              </PermissionsProvider>
            </Th>
          ))}
        </Tr>
      </Thead>

      <TBody>
        {list.map((item, index) => (
          <Tr
            key={item.code}
            data-cy={ROW}
            className={
              index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow'
            }
          >
            <Td className="px-6" data-cy={`${ROW}-currency`}>
              <span className="font-semibold">{item.code}</span>
              {item.name && (
                <span className="ml-3 text-gray-500 dark:text-gray-400">
                  {item.name}
                </span>
              )}
            </Td>
            <Td className="px-6 text-right" data-cy={`${ROW}-payout`}>
              <Amount value={item.payout} />
            </Td>
            <Td className="px-6 text-right" data-cy={`${ROW}-collection`}>
              <Amount value={item.collection} />
            </Td>
            <Td className="px-6 text-right" data-cy={`${ROW}-fee`}>
              {item.fee < 0 ? (
                <div className="flex items-center justify-end gap-4">
                  <Badge
                    label="Owes"
                    colorScheme="failed"
                    className="px-3"
                    dataCy={`${ROW}-owes`}
                  />
                  <Amount value={item.fee} />
                  <PermissionsProvider
                    permission={AfriexAdminPermission.BusinessInvoiceManage}
                  >
                    <InvoiceCreateButton
                      lockedBusinessId={businessId}
                      label="Invoice"
                      dataCy={`${ROW}-invoice`}
                      formDataCy="business-details-balances-invoice-form"
                    />
                  </PermissionsProvider>
                </div>
              ) : (
                <Amount value={item.fee} />
              )}
            </Td>
          </Tr>
        ))}
      </TBody>
    </Table>
  );
}
